import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { TranslationValues, useTranslations } from 'next-intl';
import { BaseError } from 'viem';

import { showSnackbar } from '@web/ui/components';

interface INotify {
  notifyError?: boolean;
  notifySuccess?: {
    t: ReturnType<typeof useTranslations>;
    translationKey: Parameters<ReturnType<typeof useTranslations>>[0];
    translationValues?: (data: unknown) => TranslationValues;
  };
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: INotify;
    queryMeta: INotify;
  }
}

function handleGenericError(error: Error, notify: INotify | undefined) {
  if (notify?.notifyError) {
    if (error instanceof BaseError) {
      return showSnackbar(error.shortMessage, { severity: 'error' });
    }

    return showSnackbar(error.message, { severity: 'error' });
  }
}

function handleGenericSuccess(data: unknown, notify: INotify | undefined) {
  if (notify?.notifySuccess) {
    const { t, translationKey, translationValues } = notify.notifySuccess;
    let values: TranslationValues | undefined;
    if (translationValues) {
      values = translationValues(data);
    } else if (data && typeof data === 'object') {
      values = data as TranslationValues;
    }
    showSnackbar(t(translationKey, values));
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _ctx, mutation) => {
      handleGenericError(error, mutation.options.meta);
    },
    onSuccess: (data, _variables, _ctx, mutation) => {
      handleGenericSuccess(data, mutation.options.meta);
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleGenericError(error, query.options.meta);
    },
    onSuccess: (data, query) => {
      handleGenericSuccess(data, query.options.meta);
    },
  }),
});
